const moduleState = {
  dealerId: '',
  userName: '',
  name: '',
  id: '',
  email: '',
  autolineId: '',
  rights: [],
  dealerSettings: {
    commonDriversEnabledOnB2B: null,
    commonDriversEnabledOnB2C: null,
    vwpfsFinanceEnabled: null,
    vwpfsPrivateLeaseEnabled: null,
    vwpfsOperationalLeaseEnabled: null,
    vwpfsInsuranceEnabled: null,
    insuranceProvider: null,
    autolineKeyloopEnabled: null,
    supportedVwpfsChannels: [],
    quoteValidDays: null,
    showDeliveryDates: null,
    claimOwnStockCarEnabled: null,
    pdfSigningEnabled: null,
    vwpfsInsuranceStandaardOn: null,
    vwpfsPlPriceDifferenceLimit: null,
    newStockFlowEnabled: null,
    newStockMatchingEnabled: null,
    isImporter: null,
    stockMatchingPopupEnabled: null,
    doNotDeliverBeforeEditEnabled: null,
    vcrHidden: null,
    vcrAutolineFormat: null,
    useNewAccessoryCatalog: null,
  },
}

const getters = {
  id: (state) => state.id,
  userName: (state) => state.userName, // is an emailadress...
  name: (state) => state.name,
  email: (state) => state.email,
  autolineId: (state) => state.autolineId,
  isAuthenticated: (state) => state.id != '',
  hasAccessPurchaseOrders: (state) => state.rights.includes('purchase_orders'),
  hasAccessBulkFuturePrices: (state) => state.rights.includes('bulk_future_prices'),
  hasAccessChangeOrders: (state) =>
    state.rights.includes('submit_change_order') || state.rights.includes('preview_change_order'),
  canPreviewChangeOrder: (state) => state.rights.includes('preview_change_order'),
  canModifyChangeOrder: (state) => state.rights.includes('submit_change_order'),
  canUseChangeOrders: (state, getters) =>
    !!state.dealerSettings?.changeOrderEnabled && getters.hasAccessChangeOrders,
  hasAccessAuthorizationModule: (state) => state.rights.includes('get_authorizations'),
  canGetApprovalQueue: (state) => state.rights.includes('get_approval_queue'),
  canGetApprovalAdminQueue: (state) => state.rights.includes('get_approval_queue_admin'),
  canManageWarningBanners: (state) => state.rights.includes('manage_warning_banners'),
  canManageMedia: (state) => state.rights.includes('manage_media'),
  canManageAddenda: (state) => state.rights.includes('manage_addenda'),
  canManageReports: (state) => state.rights.includes('manage_reports'),
  canManageLeaseMapping: (state) => state.rights.includes('manage_lease_mapping'),
  canManageDealerSettings: (state) => state.rights.includes('manage_dealer_settings'),
  canInvestigateConfiguration: (state) => state.rights.includes('investigate_configuration'),
  hasAdminToolsAccess: (state) => state.rights.includes('admin_tools_endpoint'),
  canViewVcr: (state) => !state.dealerSettings?.vcrHidden,
  vcrAutolineFormat: (state) => !!state.dealerSettings?.vcrAutolineFormat,
  isImporter: (state) => !!state.dealerSettings?.isImporter,
  doNotDeliverBeforeEditEnabled: (state) =>
    state.dealerSettings?.doNotDeliverBeforeEditEnabled ?? false,
  useNewAccessoryCatalog: (state) => state.dealerSettings?.useNewAccessoryCatalog ?? false,
  dealerSettings: (state) => state.dealerSettings,
  approvalModeEnabled: (state) => {
    const dealerSettings = state.dealerSettings
    const canGetApprovalQueue = state.rights.includes('get_approval_queue')
    const canGetApprovalAdminQueue = state.rights.includes('get_approval_queue_admin')
    return (
      !!dealerSettings?.salesApprovalEnabled && (canGetApprovalQueue || canGetApprovalAdminQueue)
    )
  },
}

const mutations = {
  setCurrentUser(state, payload) {
    if (!payload) {
      state.brand = ''
      state.dealerId = ''
      state.userName = ''
      state.name = ''
      state.id = ''
      state.email = ''
      state.autolineId = ''
      state.rights = []
      window.info.user = null

      return
    }
    state.brand = payload.Brand
    state.dealerId = payload.DealerId
    state.userName = payload.UserInfo.UserName
    state.name = payload.UserInfo.Name
    state.id = payload.UserInfo.Id
    state.email = payload.UserInfo.Email
    state.autolineId = payload.UserInfo.AutolineId
    let allRights = payload.Roles?.filter((role) => !!role.Rights).flatMap((role) => role.Rights)
    state.rights = [...new Set(allRights)]

    // for legacy reasons also put this info on the window
    window.info.user = payload
  },
  setDealerSettings(state, payload) {
    state.dealerSettings.brand = payload.Brand
    state.dealerSettings.commonDriversEnabledOnB2B = payload.CommonDriversEnabledOnB2B
    state.dealerSettings.commonDriversEnabledOnB2C = payload.CommonDriversEnabledOnB2C
    state.dealerSettings.stockMatchingPopupEnabled = payload.StockMatchingPopupEnabled
    state.dealerSettings.vwpfsFinanceEnabled = payload.VwpfsFinanceEnabled
    state.dealerSettings.vwpfsInsuranceEnabled = payload.VwpfsInsuranceEnabled
    state.dealerSettings.insuranceProvider = payload.InsuranceProvider
    state.dealerSettings.vwpfsPrivateLeaseEnabled = payload.VwpfsPrivateLeaseEnabled
    state.dealerSettings.vwpfsOperationalLeaseEnabled = payload.VwpfsOperationalLeaseEnabled
    state.dealerSettings.showDeliveryDates = payload.ShowDeliveryDates
    state.dealerSettings.supportedVwpfsChannels = payload.SupportedVwpfsChannels
    state.dealerSettings.quoteValidDays = payload.QuoteValidDays
    state.dealerSettings.autolineKeyloopEnabled = payload.AutolineKeyloopEnabled
    state.dealerSettings.vwpfsSilvaEnabled = payload.VwpfsSilvaEnabled
    state.dealerSettings.claimOwnStockCarEnabled = payload.ClaimOwnStockCarEnabled
    state.dealerSettings.pdfSigningEnabled = payload.PdfSigningEnabled
    state.dealerSettings.vwpfsInsuranceStandaardOn = payload.VwpfsInsuranceStandaardOn
    state.dealerSettings.changeOrderEnabled = payload.ChangeOrderEnabled
    state.dealerSettings.vwpfsPlPriceDifferenceLimit = payload.VwpfsPlPriceDifferenceLimit
    state.dealerSettings.newStockFlowEnabled = payload.NewStockFlowEnabled
    state.dealerSettings.isImporter = payload.IsImporter
    state.dealerSettings.salesApprovalEnabled = payload.SalesApprovalEnabled
    state.dealerSettings.isCanAddMultipleExchangeCars = payload.IsCanAddMultipleExchangeCars
    state.dealerSettings.newStockMatchingEnabled = payload.NewStockMatchingEnabled
    state.dealerSettings.privateLeasePrePaymentEnabled = payload.PrivateLeasePrePaymentEnabled
    state.dealerSettings.doNotDeliverBeforeEditEnabled = payload.DoNotDeliverBeforeEditEnabled
    state.dealerSettings.useNewAccessoryCatalog = payload.UseNewAccessoryCatalog
    state.dealerSettings.vcrHidden = payload.VcrHidden
    state.dealerSettings.vcrAutolineFormat = payload.VcrAutolineFormat
  },
}

export default {
  state: moduleState,
  mutations,
  getters,
  namespaced: true,
}
